/**
 * 현장서류 48시간 가격 검증 랜딩 — 지불 의사를 재는 한 장.
 *
 * 이 페이지의 목적은 설명이 아니라 **측정**이다. 실험 설계·게이트·판정 쿼리는
 * docs/experiments/field-docs/validation-2026-08.md 가 정본이고, 여기는 그 계약을 화면으로 옮긴 것뿐이다.
 *
 * 개인정보를 받지 않는다 — 이름·상호·이메일·전화·주소·자유서술·사진·영수증 입력란이 하나도 없다.
 * 폼은 전부 enum 이고, 사람과 응답을 잇는 것은 **초대코드 하나**다. 그 대응표는 저장소·로그·랜딩
 * 어디에도 두지 않고 사람이 로컬 메모로만 갖는다.
 *
 * 초대코드가 없으면(직접 주소를 친 방문 등) **아무 이벤트도 쏘지 않는다.** 화면은 그대로 보인다 —
 * 집계에 정체불명의 방문을 섞지 않기 위해서다.
 *
 * 시안: "다크 프로 작업대"(2026-08-11 사용자 선택). 다크 표면과 amber 액센트는 전부
 * 디자인 토큰에서 오고 이 페이지 안(`.fd`)으로만 스코프된다 — 헤더·푸터와 다른 페이지는 그대로다.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Container, Select, Text } from '@cottonray/ui-web'
import { SUPPORT_EMAIL } from './content'
import {
  FIELD_DOCS_PRICE,
  betaMailto,
  sendFieldDocsEvent,
  type FieldDocsBilling,
  type FieldDocsEvent,
  type FieldDocsEventInput,
  type FieldDocsMethod,
  type FieldDocsRole,
  type FieldDocsSource,
  type FieldDocsTrade,
  type FieldDocsWeeklyJobs,
} from './fieldDocsExperiment'

// 원화 표기. 통화는 그대로 KRW 다 — 화면 언어만 영어이고 대상 시장이 바뀐 게 아니다
const won = (value: number): string => `₩${value.toLocaleString('en-US')}`

const TRADES: ReadonlyArray<[FieldDocsTrade, string]> = [
  ['cleaning', 'Cleaning'],
  ['detailing', 'Mobile car wash and detailing'],
  ['handyman', 'Home repair and fitting'],
  ['landscaping', 'Landscaping and outdoor work'],
  ['other', 'Another on-site service'],
]
const ROLES: ReadonlyArray<[FieldDocsRole, string]> = [
  ['owner', 'Owner — I write the quotes'],
  ['operator', 'Worker — I only do the jobs'],
  ['other', 'Something else'],
]
const WEEKLY_JOBS: ReadonlyArray<[FieldDocsWeeklyJobs, string]> = [
  ['1-5', '1 to 5 jobs a week'],
  ['6-10', '6 to 10 jobs a week'],
  ['11-30', '11 to 30 jobs a week'],
  ['31-plus', '31 or more jobs a week'],
]
const METHODS: ReadonlyArray<[FieldDocsMethod, string]> = [
  ['paper', 'By hand, on paper'],
  ['spreadsheet', 'Excel or Google Sheets'],
  ['messenger', 'I just send photos over a messenger'],
  ['none', 'I do not make them'],
]

const SOURCES = new Set<FieldDocsSource>(['direct', 'kakao', 'sms', 'email', 'community'])
const TRADE_KEYS = new Set<FieldDocsTrade>(TRADES.map(([key]) => key))

/**
 * 초대 링크에서 문맥을 읽는다. 사람마다 다른 링크를 보내므로 업종·자격 여부는 보낼 때 이미 안다 —
 * 랜딩에서 다시 묻지 않는 이유다(물어보면 그 자체가 이탈 지점이 된다).
 *
 *   /field-docs?i=<20자리>&s=kakao&t=cleaning&q=1
 */
interface Visit {
  invite: string | null
  source: FieldDocsSource
  trade: FieldDocsTrade
  qualifiedOwner: boolean
}

function readVisit(search: string): Visit {
  const q = new URLSearchParams(search)
  const invite = q.get('i')
  const source = q.get('s') as FieldDocsSource | null
  const trade = q.get('t') as FieldDocsTrade | null
  return {
    // 형식 검증은 fieldDocsEventPath 가 한다. 여기서는 있고 없고만 본다.
    invite: invite !== null && invite !== '' ? invite : null,
    source: source !== null && SOURCES.has(source) ? source : 'direct',
    trade: trade !== null && TRADE_KEYS.has(trade) ? trade : 'other',
    qualifiedOwner: q.get('q') === '1' || q.get('q') === 'yes',
  }
}

export function FieldDocs() {
  const visit = useMemo(
    () => readVisit(typeof window === 'undefined' ? '' : window.location.search),
    [],
  )

  /**
   * 이벤트 하나가 실패해도 화면은 계속 돌아야 한다. sendBeacon 은 조용히 실패하고,
   * 초대코드 형식이 틀리면 fieldDocsEventPath 가 던진다 — 둘 다 여기서 삼킨다.
   */
  const emit = useCallback(
    (event: FieldDocsEventInput) => {
      if (visit.invite === null) return
      try {
        sendFieldDocsEvent({ ...event, invite: visit.invite } as FieldDocsEvent)
      } catch {
        // 잘못된 초대코드로 들어온 방문 — 집계 대상이 아니므로 조용히 넘긴다
      }
    },
    [visit.invite],
  )

  // 유효 방문의 분모. StrictMode 이중 실행에 두 번 쏘지 않도록 ref 로 한 번만 막는다.
  const viewSent = useRef(false)
  useEffect(() => {
    if (viewSent.current) return
    viewSent.current = true
    emit({
      name: 'landing_view',
      source: visit.source,
      trade: visit.trade,
      qualifiedOwner: visit.qualifiedOwner,
    })
  }, [emit, visit])

  return (
    <div className="fd">
      <Hero emit={emit} trade={visit.trade} />
      <Reasons />
      <Pricing emit={emit} />
      <BetaForm emit={emit} trade={visit.trade} inbound={visit.invite === null} />
    </div>
  )
}

type Emit = (event: FieldDocsEventInput) => void

function Hero({ emit, trade }: { emit: Emit; trade: FieldDocsTrade }) {
  const [sampleOpen, setSampleOpen] = useState(false)

  const openSample = () => {
    if (!sampleOpen) emit({ name: 'sample_pdf_open', trade })
    setSampleOpen((open) => !open)
  }

  return (
    <section className="fd-hero">
      <Container>
        <div className="fd-hero__grid">
          <div className="fd-hero__copy">
            <Text tone="muted" className="fd-kicker">
              Stop staying up doing paperwork after the job is done.
            </Text>
            <h1 className="fd-h1 st-text">
              Take the photos and
              <br />
              the quote and the report
              <br />
              <span className="fd-h1__accent">come out together</span>
            </h1>
            <Text tone="muted" className="fd-sub">
              Group the before and after photos, the receipts, and a short note into one job, then
              share a VAT-inclusive quote and a customer-facing completion report as a PDF right
              away.
            </Text>

            <div className="fd-cta">
              <a className="st-btn st-btn--primary st-btn--lg fd-cta__main" href="#pricing">
                Try it at {won(FIELD_DOCS_PRICE.monthly)} a month
              </a>
              <Button variant="secondary" size="lg" onClick={openSample}>
                {sampleOpen ? 'Hide the sample' : 'See a sample report'}
              </Button>
            </div>

            <Text tone="subtle" className="fd-trust">
              First 3 jobs free · {won(FIELD_DOCS_PRICE.yearly)} a year
            </Text>
          </div>

          <div className="fd-hero__doc">
            <SampleReport expanded={sampleOpen} />
          </div>
        </div>
      </Container>
    </section>
  )
}

/**
 * 샘플 완료보고서. **실존 인물·업체를 쓰지 않는다** — 값은 전부 자리표시고 상단에 샘플이라고 적는다.
 * (실험 원장의 원안은 "정적 PDF 한 개"였지만, 선택된 시안이 문서를 화면 안에 그대로 보여 주는
 * 구성이라 인라인으로 뒀다. 다운로드 마찰이 없어 열람률이 높은 쪽이기도 하다.)
 */
function SampleReport({ expanded }: { expanded: boolean }) {
  return (
    <div className="fd-doc" aria-label="Sample completion report">
      <div className="fd-doc__head">
        <Text variant="heading">Completion report</Text>
        <span className="fd-doc__stamp">Sample</span>
      </div>
      <dl className="fd-doc__meta">
        <div>
          <dt>Job</dt>
          <dd>Move-in cleaning, apartment (112 m²)</dd>
        </div>
        <div>
          <dt>Date</dt>
          <dd>0000. 00. 00</dd>
        </div>
      </dl>

      <table className="fd-doc__table">
        <thead>
          <tr>
            <th scope="col">Item</th>
            <th scope="col">Qty</th>
            <th scope="col">Unit price</th>
            <th scope="col">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Move-in cleaning (112 m²)</td>
            <td>1</td>
            <td>280,000</td>
            <td>280,000</td>
          </tr>
          <tr>
            <td>Add-on — steam sanitising</td>
            <td>1</td>
            <td>30,000</td>
            <td>30,000</td>
          </tr>
          {expanded && (
            <tr>
              <td>Materials</td>
              <td>1</td>
              <td>20,000</td>
              <td>20,000</td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3}>Subtotal</td>
            <td>{expanded ? '330,000' : '310,000'}</td>
          </tr>
          <tr>
            <td colSpan={3}>VAT (10%)</td>
            <td>{expanded ? '33,000' : '31,000'}</td>
          </tr>
          <tr className="fd-doc__total">
            <td colSpan={3}>Total</td>
            <td>{expanded ? '₩363,000' : '₩341,000'}</td>
          </tr>
        </tfoot>
      </table>

      {expanded && (
        <p className="fd-doc__memo st-text">
          Job notes — deep-cleaned the grease off the kitchen hood. Removed mould from the bathroom
          silicone and re-sealed it. Cleaned the window frames and insect screens.
        </p>
      )}
    </div>
  )
}

const REASONS = [
  {
    title: 'Before and after pair themselves up',
    body: 'Shots of the same spot are matched in the order you took them, so the customer sees what changed at a glance.',
  },
  {
    title: 'Materials, labour, and VAT add up in one place',
    body: 'Enter the lines and the subtotal and VAT are worked out for you, so fewer things fall out of the sum.',
  },
  {
    title: 'The customer gets one PDF',
    body: 'The completion report becomes a PDF you send through the messenger you already use. Your customer installs nothing.',
  },
] as const

function Reasons() {
  return (
    <section className="fd-band">
      <Container>
        <div className="fd-band__head">
          <Text variant="title">Three things that finish the paperwork once a day</Text>
        </div>
        <div className="fd-reasons">
          {REASONS.map((reason, index) => (
            <div key={reason.title} className="fd-reason">
              <Text variant="label" tone="accent" as="span" className="fd-reason__no">
                {String(index + 1).padStart(2, '0')}
              </Text>
              <Text variant="heading">{reason.title}</Text>
              <Text tone="muted" className="st-text">
                {reason.body}
              </Text>
            </div>
          ))}
        </div>
      </Container>
    </section>
  )
}

const PLANS: ReadonlyArray<{ billing: FieldDocsBilling; label: string; note: string }> = [
  { billing: 'monthly', label: 'Monthly', note: 'Stop whenever you like' },
  { billing: 'yearly', label: 'Yearly', note: 'Two months cheaper' },
]

function Pricing({ emit }: { emit: Emit }) {
  const ref = useRef<HTMLElement>(null)
  const [clicked, setClicked] = useState<FieldDocsBilling | null>(null)

  // 가격이 실제로 눈에 들어왔을 때만 pricing_view 를 센다 — 스크롤로 지나친 것은 노출이 아니다.
  const viewSent = useRef(false)
  useEffect(() => {
    const node = ref.current
    if (node === null || viewSent.current) return
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || viewSent.current) continue
          viewSent.current = true
          emit({ name: 'pricing_view' })
          observer.disconnect()
        }
      },
      { threshold: 0.5 },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [emit])

  return (
    <section className="fd-band fd-band--pricing" id="pricing" ref={ref}>
      <Container>
        <div className="fd-band__head">
          <Text variant="title">Pricing</Text>
          <Text tone="muted">
            The first three jobs are free. If it does not suit you, stop.
          </Text>
        </div>
        <div className="fd-plans">
          {PLANS.map((plan) => (
            <div key={plan.billing} className="fd-plan">
              <Text variant="label" tone="muted" as="span">
                {plan.label}
              </Text>
              <p className="fd-plan__price">{won(FIELD_DOCS_PRICE[plan.billing])}</p>
              <Text tone="subtle" as="span">
                {plan.note}
              </Text>
              <Button
                variant={plan.billing === 'monthly' ? 'primary' : 'secondary'}
                fullWidth
                onClick={() => {
                  emit({ name: 'plan_click', billing: plan.billing })
                  setClicked(plan.billing)
                }}
              >
                Request a trial on this plan
              </Button>
            </div>
          ))}
        </div>
        {clicked !== null && (
          <Text tone="accent" className="fd-plan__ack st-text">
            Pick the four answers below and we will open access in order as places free up.
          </Text>
        )}
      </Container>
    </section>
  )
}

/**
 * `inbound` 는 **초대코드 없이 들어온 사람**이다 — 검색·홈의 무료 도구에서 흘러온 낯선 방문자.
 *
 * 이 폼은 원래 아웃바운드 전용이었다. 끝 화면이 "연락드렸던 곳에 beta 라고 답장하세요"인데,
 * 검색으로 들어온 사람에게는 **연락드렸던 곳이 없다.** 즉 무료 도구로 유입을 만들어 봐야
 * 여기서 전부 증발했다 (2026-08-25에 발견).
 *
 * 고쳐도 개인정보는 여전히 받지 않는다. 고른 네 답을 실어 **본인의 메일 앱**을 여는 링크를 줄
 * 뿐이고, 보낼지 말지는 본인이 정한다. 사이트가 저장하는 것도 보내는 것도 없다.
 */
function BetaForm({
  emit,
  trade,
  inbound,
}: {
  emit: Emit
  trade: FieldDocsTrade
  inbound: boolean
}) {
  const [form, setForm] = useState({
    trade,
    role: 'owner' as FieldDocsRole,
    weeklyJobs: '1-5' as FieldDocsWeeklyJobs,
    method: 'paper' as FieldDocsMethod,
  })
  const [submitted, setSubmitted] = useState(false)

  // 폼을 "시작했다"는 것은 첫 선택이 바뀐 순간이다. 화면에 떠 있기만 한 것은 시작이 아니다.
  const startSent = useRef(false)
  const markStarted = () => {
    if (startSent.current) return
    startSent.current = true
    emit({ name: 'beta_form_start', trade: form.trade })
  }

  if (submitted) {
    return (
      <section className="fd-band fd-band--form">
        <Container>
          <div className="fd-done">
            <Text variant="title">Got it</Text>
            {inbound ? (
              <>
                <Text tone="muted" className="st-text">
                  We have no way to reach you yet. Send this one line and you are in the queue —
                  your mail app opens with the four answers already filled in.
                </Text>
                <Button
                  variant="primary"
                  onClick={() => {
                    window.location.href = betaMailto(form)
                  }}
                >
                  Open the email
                </Button>
                <Text tone="muted" className="st-text">
                  Or write to <strong>{SUPPORT_EMAIL}</strong> yourself.
                </Text>
              </>
            ) : (
              <Text tone="muted" className="st-text">
                Reply <strong>beta</strong> wherever we reached you and we will put you in the queue.
                There is nothing else to fill in.
              </Text>
            )}
          </div>
        </Container>
      </section>
    )
  }

  return (
    <section className="fd-band fd-band--form">
      <Container>
        <div className="fd-band__head">
          <Text variant="title">Request a trial</Text>
          <Text tone="muted" className="st-text">
            Choosing is all there is to it. We ask for no name, contact details, or company name.
          </Text>
        </div>

        <form
          className="fd-form"
          onSubmit={(event) => {
            event.preventDefault()
            emit({
              name: 'beta_form_submit',
              trade: form.trade,
              role: form.role,
              weeklyJobs: form.weeklyJobs,
              method: form.method,
            })
            setSubmitted(true)
          }}
        >
          <Field label="What kind of work do you do?">
            <Select
              value={form.trade}
              onChange={(event) => {
                markStarted()
                setForm((prev) => ({ ...prev, trade: event.target.value as FieldDocsTrade }))
              }}
            >
              {TRADES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="What is your role on site?">
            <Select
              value={form.role}
              onChange={(event) => {
                markStarted()
                setForm((prev) => ({ ...prev, role: event.target.value as FieldDocsRole }))
              }}
            >
              {ROLES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="How many jobs do you do in a week?">
            <Select
              value={form.weeklyJobs}
              onChange={(event) => {
                markStarted()
                setForm((prev) => ({
                  ...prev,
                  weeklyJobs: event.target.value as FieldDocsWeeklyJobs,
                }))
              }}
            >
              {WEEKLY_JOBS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="How do you produce the paperwork today?">
            <Select
              value={form.method}
              onChange={(event) => {
                markStarted()
                setForm((prev) => ({ ...prev, method: event.target.value as FieldDocsMethod }))
              }}
            >
              {METHODS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>

          <Button type="submit" size="lg" fullWidth>
            Request a trial
          </Button>
        </form>

        <Text tone="subtle" className="fd-privacy st-text">
          This page takes no name, company name, email address, phone number, address, photo, or
          receipt. Access records (IP address and browser information) are kept for 14 days under
          the server operations log policy.
        </Text>
      </Container>
    </section>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="fd-field">
      <Text variant="label" tone="muted" as="span">
        {label}
      </Text>
      {children}
    </label>
  )
}
